import React from 'react';
import { SHADE_PRESETS } from '../../../utils/smileTemplates';

const TOOLS = [
  { id: 'brush', label: 'Brush', shortcut: 'B', icon: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z' },
  { id: 'eraser', label: 'Eraser', shortcut: 'E', icon: 'M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88' },
  { id: 'whitening', label: 'Whiten', shortcut: 'W', icon: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z' },
  { id: 'rectangle', label: 'Rectangle', shortcut: 'R', icon: 'M5.25 7.5A2.25 2.25 0 017.5 5.25h9a2.25 2.25 0 012.25 2.25v9a2.25 2.25 0 01-2.25 2.25h-9a2.25 2.25 0 01-2.25-2.25v-9z' },
  { id: 'ellipse', label: 'Ellipse', shortcut: 'O', icon: 'M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25' },
  { id: 'text', label: 'Text', shortcut: 'T', icon: 'M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z' },
];

const DrawingToolsPanel = ({ activeTool, onToolChange, drawSettings, onDrawSettingsChange }) => {
  const updateSetting = (key, value) => {
    onDrawSettingsChange(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div>
      <h3 className="text-sm font-bold text-gray-800 mb-3">Drawing Tools</h3>

      {/* Tool Grid */}
      <div className="grid grid-cols-3 gap-1.5 mb-4">
        {TOOLS.map(tool => (
          <button
            key={tool.id}
            onClick={() => onToolChange(tool.id)}
            className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition ${
              activeTool === tool.id
                ? 'border-blue-400 bg-blue-50 text-blue-600'
                : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
            }`}
            title={`${tool.label} (${tool.shortcut})`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d={tool.icon} />
            </svg>
            <span className="text-[9px] font-medium">{tool.label}</span>
          </button>
        ))}
      </div>

      {/* Brush/Eraser Settings */}
      {['brush', 'eraser', 'whitening'].includes(activeTool) && (
        <div className="space-y-3 mb-4 p-3 bg-gray-50 rounded-lg">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[11px] font-medium text-gray-600">
                {activeTool === 'eraser' ? 'Eraser' : 'Brush'} Size
              </label>
              <span className="text-[11px] text-gray-400">
                {activeTool === 'eraser' ? drawSettings.eraserWidth : activeTool === 'whitening' ? drawSettings.whiteningWidth : drawSettings.brushWidth}px
              </span>
            </div>
            <input
              type="range"
              min={1}
              max={80}
              value={activeTool === 'eraser' ? drawSettings.eraserWidth : activeTool === 'whitening' ? drawSettings.whiteningWidth : drawSettings.brushWidth}
              onChange={(e) => {
                const val = parseInt(e.target.value);
                if (activeTool === 'eraser') updateSetting('eraserWidth', val);
                else if (activeTool === 'whitening') updateSetting('whiteningWidth', val);
                else updateSetting('brushWidth', val);
              }}
              className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>

          {activeTool !== 'eraser' && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] font-medium text-gray-600">Opacity</label>
                <span className="text-[11px] text-gray-400">
                  {Math.round((activeTool === 'whitening' ? drawSettings.whiteningOpacity : drawSettings.brushOpacity) * 100)}%
                </span>
              </div>
              <input
                type="range"
                min={0.05}
                max={1}
                step={0.05}
                value={activeTool === 'whitening' ? drawSettings.whiteningOpacity : drawSettings.brushOpacity}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  if (activeTool === 'whitening') updateSetting('whiteningOpacity', val);
                  else updateSetting('brushOpacity', val);
                }}
                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>
          )}
        </div>
      )}

      {/* Color Picker (for brush, shapes, text) */}
      {['brush', 'rectangle', 'ellipse', 'text'].includes(activeTool) && (
        <div className="mb-4">
          <label className="text-[11px] font-medium text-gray-600 mb-1.5 block">Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={activeTool === 'text' ? drawSettings.fontColor : activeTool === 'brush' ? drawSettings.brushColor : drawSettings.shapeStroke}
              onChange={(e) => {
                if (activeTool === 'text') updateSetting('fontColor', e.target.value);
                else if (activeTool === 'brush') updateSetting('brushColor', e.target.value);
                else updateSetting('shapeStroke', e.target.value);
              }}
              className="w-8 h-8 rounded cursor-pointer border border-gray-200"
            />
            {/* Quick tooth shade swatches */}
            <div className="flex gap-1">
              {SHADE_PRESETS.map(shade => (
                <button
                  key={shade.id}
                  onClick={() => {
                    if (activeTool === 'text') updateSetting('fontColor', shade.color);
                    else if (activeTool === 'brush') updateSetting('brushColor', shade.color);
                    else updateSetting('shapeStroke', shade.color);
                  }}
                  className="w-6 h-6 rounded-full border border-gray-300 hover:ring-2 hover:ring-blue-300"
                  style={{ backgroundColor: shade.color }}
                  title={shade.name}
                />
              ))}
            </div>
          </div>

          {/* Common colors */}
          <div className="flex gap-1 mt-2">
            {['#000000', '#FFFFFF', '#EF4444', '#3B82F6', '#10B981', '#F59E0B'].map(color => (
              <button
                key={color}
                onClick={() => {
                  if (activeTool === 'text') updateSetting('fontColor', color);
                  else if (activeTool === 'brush') updateSetting('brushColor', color);
                  else updateSetting('shapeStroke', color);
                }}
                className="w-5 h-5 rounded-full border border-gray-300 hover:ring-2 hover:ring-blue-300"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Shape fill/stroke settings */}
      {['rectangle', 'ellipse'].includes(activeTool) && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="mb-2">
            <label className="text-[11px] font-medium text-gray-600 mb-1 block">Fill</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={drawSettings.shapeFill === 'transparent' ? '#ffffff' : drawSettings.shapeFill}
                onChange={(e) => updateSetting('shapeFill', e.target.value)}
                className="w-6 h-6 rounded cursor-pointer border border-gray-200"
              />
              <button
                onClick={() => updateSetting('shapeFill', 'transparent')}
                className={`text-[10px] px-2 py-0.5 rounded border ${
                  drawSettings.shapeFill === 'transparent'
                    ? 'border-blue-400 bg-blue-50 text-blue-600'
                    : 'border-gray-200 text-gray-500'
                }`}
              >
                None
              </button>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[11px] font-medium text-gray-600">Stroke Width</label>
              <span className="text-[11px] text-gray-400">{drawSettings.shapeStrokeWidth}px</span>
            </div>
            <input
              type="range"
              min={1}
              max={10}
              value={drawSettings.shapeStrokeWidth}
              onChange={(e) => updateSetting('shapeStrokeWidth', parseInt(e.target.value))}
              className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>
        </div>
      )}

      {/* Text settings */}
      {activeTool === 'text' && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] font-medium text-gray-600">Font Size</label>
            <span className="text-[11px] text-gray-400">{drawSettings.fontSize}px</span>
          </div>
          <input
            type="range"
            min={8}
            max={72}
            value={drawSettings.fontSize}
            onChange={(e) => updateSetting('fontSize', parseInt(e.target.value))}
            className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
        </div>
      )}
    </div>
  );
};

export default DrawingToolsPanel;
